/* eslint-disable max-len */
import { ofType } from 'redux-observable';
import {
  catchError, debounceTime, filter, from, map, mergeMap, Observable, of, takeUntil,
} from 'rxjs';
import { AxiosError } from 'axios';
import { showErrorToaster, showSuccessToaster } from '../../../../Common/ComponentToast/ComponentSuccessToasts';
import {
  AnalystListSliceActions, deleteAnalyst,
  deleteAnalystFailure, deleteAnalystSuccess,
  getAnalystData, getAnalystDataFailure, getAnalystDataSuccess,
  getCarrierData, getLevelData, getTeritoryData, gotCarrierData, gotLevelData, gotTeritoryData,
  onArchiveAnalyst,
  onArchiveAnalystFailure,
  onArchiveAnalystSuccess,
} from './AnalystListSlice';
import {
  AnalystQueryPayload, AnalystResponseFormat, ArchiveAnalystInterface, CarrierResponseFormat, DeleteAnalyticPayload, DeleteResponseFormat, TeritoryResponseFormat,
} from '../utils/TypeAnalyst';
import Config from '../../../../Common/Config';
import { makeDeleteRequest, makeGetRequest, makeGetRequestWithParams } from '../../../../Common/NetworkOps';
import { UserManagementListDataResponse } from '../../../UserManagement/redux/type';

async function getAnalystListData(data: AnalystQueryPayload): Promise<AnalystResponseFormat> {
  const {
    page, rowsPerPage, searchQuery, sortCol, sortDir,
  } = data;
  const url = `${Config.analyst.analystlist}?PageNumber=${page}&PageSize=${rowsPerPage}&searchItem=${searchQuery}&sortColumn=${sortCol}&sortDirection=${sortDir}`;
  const result = await makeGetRequest<AnalystResponseFormat>(url);
  return result.data;
}

export const getAnalystDataEpic = (action$: Observable<AnalystListSliceActions>) => action$.pipe(
  ofType(getAnalystData.type),
  map((x) => x.payload),
  debounceTime(500),
  mergeMap((data: AnalystQueryPayload) => from(getAnalystListData(data)).pipe(
    map((res: AnalystResponseFormat) => {
      if (res.BMT.ResponseCode === Config.SUCCESS_CODE) {
        return getAnalystDataSuccess(res.BMT.Result);
      }
      showErrorToaster(res.BMT.ResponseMessage);
      return getAnalystDataFailure();
    }),
    takeUntil(action$.pipe(filter(getAnalystData.match))),
    catchError((error) => of(getAnalystDataFailure(error))),
  )),
);

async function deleteAnalystFromList(data: DeleteAnalyticPayload): Promise<DeleteResponseFormat> {
  const url = `${Config.analyst.deleteAnalyst}?AnalystId=${data.analyticId}`;
  const result = await makeDeleteRequest<DeleteResponseFormat>(url);
  return result.data;
}

export const deleteAnalystEpic = (action$: Observable<AnalystListSliceActions>) => action$.pipe(
  ofType(deleteAnalyst.type),
  map((x) => x.payload),
  mergeMap((data: DeleteAnalyticPayload) => from(deleteAnalystFromList(data)).pipe(
    mergeMap((res: DeleteResponseFormat) => {
      if (res.BMT.ResponseCode === Config.SUCCESS_CODE) {
        const payload = {
          page: data?.page,
          rowsPerPage: data?.rowsPerPage,
          searchQuery: data?.searchQuery,
        };
        showSuccessToaster(res.BMT.ResponseMessage);
        return of(deleteAnalystSuccess(), getAnalystData(payload));
      }
      showErrorToaster(res.BMT.ResponseMessage);
      return of(deleteAnalystFailure());
    }),
    takeUntil(action$.pipe(filter(deleteAnalyst.match))),
    catchError((error) => of(deleteAnalystFailure(error))),
  )),
);

async function getCarrierListData(): Promise<CarrierResponseFormat> {
  const url = `${Config.analyst.getCarrier}`;
  const result = await makeGetRequest<CarrierResponseFormat>(url);
  return result.data;
}

export const getCarrierDataEpic = (action$: Observable<AnalystListSliceActions>) => action$.pipe(
  ofType(getCarrierData.type),
  map((x) => x.payload),
  mergeMap(() => from(getCarrierListData()).pipe(
    map((res: CarrierResponseFormat) => {
      if (res.BMT.ResponseCode === Config.SUCCESS_CODE) {
        return gotCarrierData(res.BMT.Result);
      }
      showErrorToaster(res.BMT.ResponseMessage);
      return getAnalystDataFailure();
    }),
    takeUntil(action$.pipe(filter(getCarrierData.match))),
    catchError((error) => of(getAnalystDataFailure(error))),
  )),
);

async function getTeritoryListData(): Promise<TeritoryResponseFormat> {
  const url = `${Config.analyst.getTeritory}`;
  const result = await makeGetRequest<TeritoryResponseFormat>(url);
  return result.data;
}

export const getTeritoryDataEpic = (action$: Observable<AnalystListSliceActions>) => action$.pipe(
  ofType(getTeritoryData.type),
  map((x) => x.payload),
  mergeMap(() => from(getTeritoryListData()).pipe(
    map((res: TeritoryResponseFormat) => {
      if (res.BMT.ResponseCode === Config.SUCCESS_CODE) {
        return gotTeritoryData(res.BMT.Result);
      }
      showErrorToaster(res.BMT.ResponseMessage);
      return of(getAnalystDataFailure());
    }),
    takeUntil(action$.pipe(filter(getTeritoryData.match))),
    catchError((error) => of(getAnalystDataFailure(error))),
  )),
);
async function getLevelListData(): Promise<TeritoryResponseFormat> {
  const url = `${Config.analyst.getLevels}`;
  const result = await makeGetRequest<TeritoryResponseFormat>(url);
  return result.data;
}

export const getLevelDataEpic = (action$: Observable<AnalystListSliceActions>) => action$.pipe(
  ofType(getLevelData.type),
  map((x) => x.payload),
  mergeMap(() => from(getLevelListData()).pipe(
    map((res: TeritoryResponseFormat) => {
      if (res.BMT.ResponseCode === Config.SUCCESS_CODE) {
        return gotLevelData(res.BMT.Result);
      }
      showErrorToaster(res.BMT.ResponseMessage);
      return of(getAnalystDataFailure());
    }),
    takeUntil(action$.pipe(filter(getLevelData.match))),
    catchError((error) => of(getAnalystDataFailure(error))),
  )),
);
async function onArchiveAnalystApiCall(payload: object): Promise<UserManagementListDataResponse> {
  const url = `${Config.analyst.archiveAnalyst}`;
  const result = await makeGetRequestWithParams<UserManagementListDataResponse>(url, payload);
  return result?.data;
}

export const epicOnArchiveAnalyst = (action$: Observable<AnalystListSliceActions>) => action$.pipe(
  filter(onArchiveAnalyst.match),
  debounceTime(250),
  map((x) => x.payload),
  mergeMap(({ payload, callback }: ArchiveAnalystInterface) => from(onArchiveAnalystApiCall(payload)).pipe(
    map((res: UserManagementListDataResponse) => {
      if (res.BMT.ResponseCode === Config.SUCCESS_CODE) {
        showSuccessToaster(res.BMT.ResponseMessage);
        callback();
        return onArchiveAnalystSuccess();
      }
      showErrorToaster(res.BMT.ResponseMessage);
      callback();
      return onArchiveAnalystFailure();
    }),
    takeUntil(action$.pipe(filter(onArchiveAnalyst.match))),
    catchError((error: AxiosError<UserManagementListDataResponse>) => {
      const errorMessage = error?.response?.data?.BMT?.ResponseMessage as string;
      of(showErrorToaster(errorMessage));
      callback();
      return of(onArchiveAnalystFailure());
    }),
  )),
);
